/**
 * Human-readable byte-size formatter for the contracts file list.
 * @module features/contracts/utils/formatFileSize
 */

const UNITS = ["B", "KB", "MB", "GB", "TB"] as const;
const STEP = 1024;

/**
 * Formats a byte count into a short human-readable string.
 * @param bytes - Non-negative byte count to format. Negative or NaN values
 *   are coerced to `0` rather than throwing.
 * @returns A string like `"1.5 MB"` (one decimal place from KB upward,
 *   no decimals for raw bytes).
 * @example
 * formatFileSize(0)            // "0 B"
 * formatFileSize(900)          // "900 B"
 * formatFileSize(1024)         // "1.0 KB"
 * formatFileSize(1024 * 1500)  // "1.5 MB"
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const maxUnitIndex = UNITS.length - 1;
  const rawIndex = Math.floor(Math.log(bytes) / Math.log(STEP));
  const unitIndex = Math.min(rawIndex, maxUnitIndex);
  const size = bytes / STEP ** unitIndex;
  const formatted = unitIndex === 0 ? size.toFixed(0) : size.toFixed(1);

  return `${formatted} ${UNITS[unitIndex]}`;
}
